import { jsPDF } from "jspdf";
import { money, fmtDate, computeTotalsPerLine, DOC_META } from "@/lib/format";

const ORANGE = [227, 89, 42];
const DARK = [17, 24, 39];
const GREY = [107, 114, 128];

export function generateDocPdf(doc, user, logoDataUrl) {
  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const M = 48;
  let y = 54;
  const meta = DOC_META[doc.type] || { label: doc.type };
  const data = doc.data || {};

  pdf.setFillColor(...ORANGE);
  pdf.rect(0, 0, W, 8, "F");

  if (logoDataUrl) {
    try {
      const fmt = logoDataUrl.includes("image/png") ? "PNG" : "JPEG";
      pdf.addImage(logoDataUrl, fmt, M, y, 54, 54);
    } catch (e) {}
  }
  const textX = logoDataUrl ? M + 66 : M;
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(18); pdf.setTextColor(...DARK);
  pdf.text(user.business_name || "Your Business", textX, y + 16);
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(10); pdf.setTextColor(...GREY);
  pdf.text(user.trade_label || "", textX, y + 31);
  const addr = user.address || {};
  const addrLine = [addr.line1, addr.line2, addr.city, addr.postcode].filter(Boolean).join(", ");
  let hy = y + 45;
  if (addrLine) { pdf.text(addrLine, textX, hy, { maxWidth: W / 2 }); hy += 14; }
  if (user.vat_registered && user.vat_number) { pdf.text(`VAT No: ${user.vat_number}`, textX, hy); }

  pdf.setFont("helvetica", "bold"); pdf.setFontSize(22); pdf.setTextColor(...ORANGE);
  pdf.text(meta.label.toUpperCase(), W - M, y + 16, { align: "right" });
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(11); pdf.setTextColor(...DARK);
  pdf.text(doc.number || "", W - M, y + 34, { align: "right" });

  y += 96;
  pdf.setDrawColor(229, 231, 235); pdf.line(M, y, W - M, y); y += 24;

  // Client (left)
  const client = doc.client || {};
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(9); pdf.setTextColor(...GREY);
  pdf.text("PREPARED FOR", M, y);
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(13); pdf.setTextColor(...DARK);
  pdf.text(client.name || "—", M, y + 18);
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(10); pdf.setTextColor(...GREY);
  let cy = y + 34;
  [client.address, client.email, client.phone].filter(Boolean).forEach((l) => { const ll = pdf.splitTextToSize(String(l), W / 2 - M); pdf.text(ll, M, cy); cy += ll.length * 13; });
  if (data.site_address) { pdf.setFont("helvetica", "bold"); pdf.text("Site: ", M, cy); pdf.setFont("helvetica", "normal"); const sl = pdf.splitTextToSize(String(data.site_address), W / 2 - M - 26); pdf.text(sl, M + 26, cy); cy += sl.length * 13; }

  // Dates (right)
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(10); pdf.setTextColor(...DARK);
  let ry = y + 2;
  const rline = (t) => { if (!t) return; pdf.text(t, W - M, ry, { align: "right" }); ry += 15; };
  const dDate = data.doc_date || doc.created_at;
  if (doc.type === "quote") { rline(`Quote date: ${fmtDate(dDate)}`); if (data.valid_until) rline(`Valid until: ${fmtDate(data.valid_until)}`); }
  if (doc.type === "invoice") { rline(`Invoice date: ${fmtDate(dDate)}`); if (data.supply_date) rline(`Date of supply: ${fmtDate(data.supply_date)}`); if (data.due_date) rline(`Payment due: ${fmtDate(data.due_date)}`); }
  if (doc.type === "waste_note") rline(`Date: ${fmtDate(dDate)}`);

  y = Math.max(cy, ry) + 16;

  if (data.title) { pdf.setFont("helvetica", "bold"); pdf.setFontSize(13); pdf.setTextColor(...DARK); pdf.text(String(data.title), M, y); y += 22; }

  y = doc.type === "waste_note" ? drawWaste(pdf, doc, user, y, M, W, H) : drawLineItems(pdf, data, y, M, W);

  // Notes
  if (data.notes) {
    y += 16;
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(9); pdf.setTextColor(...GREY); pdf.text("NOTES", M, y);
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(10); pdf.setTextColor(...DARK);
    const lines = pdf.splitTextToSize(String(data.notes), W - 2 * M);
    pdf.text(lines, M, y + 14); y += 14 + lines.length * 13;
  }
  // Quote terms + deposit
  if (doc.type === "quote") {
    if (data.deposit) { y += 14; pdf.setFont("helvetica", "bold"); pdf.setFontSize(9); pdf.setTextColor(...GREY); pdf.text("DEPOSIT / PAYMENT TERMS", M, y); pdf.setFont("helvetica", "normal"); pdf.setFontSize(10); pdf.setTextColor(...DARK); const dl = pdf.splitTextToSize(String(data.deposit), W - 2 * M); pdf.text(dl, M, y + 14); y += 14 + dl.length * 13; }
    if (data.terms) { y += 12; pdf.setFont("helvetica", "bold"); pdf.setFontSize(9); pdf.setTextColor(...GREY); pdf.text("TERMS & CONDITIONS", M, y); pdf.setFont("helvetica", "normal"); pdf.setFontSize(9); pdf.setTextColor(...DARK); const tl = pdf.splitTextToSize(String(data.terms), W - 2 * M); pdf.text(tl, M, y + 14); y += 14 + tl.length * 12; }
  }
  // Invoice payment
  if (doc.type === "invoice") {
    const extras = [];
    if (data.payment_details) extras.push(`Payment details: ${data.payment_details}`);
    if (data.payment_terms) extras.push(data.payment_terms);
    if (extras.length) { y += 14; pdf.setFont("helvetica", "normal"); pdf.setFontSize(9); pdf.setTextColor(...GREY); extras.forEach((e) => { const l = pdf.splitTextToSize(String(e), W - 2 * M); pdf.text(l, M, y); y += l.length * 12 + 4; }); }
  }

  // Footer
  const footBits = [];
  const legal = user.legal || {};
  Object.entries(legal).forEach(([k, v]) => { if (v) footBits.push(`${labelize(k)}: ${v}`); });
  if (user.carrier_licence_expiry) footBits.push(`Carrier licence expires: ${fmtDate(user.carrier_licence_expiry)}`);
  const foot = [user.phone, user.email].filter(Boolean).concat(footBits).join("  •  ");
  pdf.setDrawColor(229, 231, 235); pdf.line(M, H - 56, W - M, H - 56);
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(8); pdf.setTextColor(...GREY);
  pdf.text(pdf.splitTextToSize(foot, W - 2 * M), W / 2, H - 42, { align: "center" });

  return pdf;
}

function drawLineItems(pdf, data, y, M, W) {
  const items = data.line_items || [];
  const hasVat = items.some((li) => Number(li.vat_rate)) || Number(data.tax_rate);
  const xAmt = W - M;
  const xVat = W - M - 66;
  const xUnit = W - M - (hasVat ? 150 : 110);
  const xQty = W - M - (hasVat ? 210 : 170);
  pdf.setFillColor(249, 250, 251); pdf.rect(M, y, W - 2 * M, 24, "F");
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(9); pdf.setTextColor(...GREY);
  pdf.text("DESCRIPTION", M + 8, y + 16);
  pdf.text("QTY", xQty, y + 16);
  pdf.text("UNIT", xUnit, y + 16);
  if (hasVat) pdf.text("VAT", xVat, y + 16);
  pdf.text("AMOUNT", xAmt, y + 16, { align: "right" });
  y += 24;
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(10); pdf.setTextColor(...DARK);
  items.forEach((li) => {
    const amt = Number(li.qty || 0) * Number(li.unit_price || 0);
    const dl = pdf.splitTextToSize(String(li.description || ""), xQty - M - 16);
    pdf.text(dl, M + 8, y + 15);
    pdf.text(String(li.qty || 0), xQty, y + 15);
    pdf.text(money(li.unit_price), xUnit, y + 15);
    if (hasVat) pdf.text(`${Number(li.vat_rate || 0)}%`, xVat, y + 15);
    pdf.text(money(amt), xAmt, y + 15, { align: "right" });
    y += Math.max(dl.length * 13, 15) + 8;
    pdf.setDrawColor(240, 240, 240); pdf.line(M, y, W - M, y);
  });
  const t = computeTotalsPerLine(items, data.tax_rate || 0);
  y += 14;
  const rx = W - M; const lx = W - M - 180;
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(10);
  pdf.setTextColor(...GREY); pdf.text("Subtotal", lx, y); pdf.setTextColor(...DARK); pdf.text(money(t.subtotal), rx, y, { align: "right" }); y += 16;
  if (t.tax > 0) { pdf.setTextColor(...GREY); pdf.text("VAT", lx, y); pdf.setTextColor(...DARK); pdf.text(money(t.tax), rx, y, { align: "right" }); y += 16; }
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(13); pdf.setTextColor(...ORANGE);
  pdf.text("Total", lx, y + 4); pdf.text(money(t.total), rx, y + 4, { align: "right" });
  return y + 20;
}

function drawWaste(pdf, doc, user, y, M, W, H) {
  const d = doc.data || {};
  const client = doc.client || {};
  const legal = user.legal || {};
  const container = d.container === "container" ? [d.container_type, d.container_size].filter(Boolean).join(" ") || "Container" : "Loose";
  const qty = [d.quantity, d.quantity_unit].filter(Boolean).join(" ");
  const transfer = [fmtDate(d.date_of_transfer), d.time_of_transfer].filter(Boolean).join(" ");
  const dest = [d.destination_name, d.destination_permit && `(permit ${d.destination_permit})`].filter(Boolean).join(" ");
  const rows = [
    ["Description of waste", d.description],
    ["EWC / List of Waste code", d.ewc_code],
    ["Quantity", qty],
    ["Loose or container", container],
    ["Date & time of transfer", transfer],
    ["Place of transfer", d.place_of_transfer],
    ["Client SIC code", d.sic_code],
    ["Permit / exemption", d.permit_details],
    ["Broker / dealer", d.broker],
    ["Destination", dest],
    ["Carrier registration no.", legal.waste_carrier_licence],
  ].filter((r) => r[1]);
  pdf.setFontSize(10);
  rows.forEach(([k, v]) => {
    pdf.setFont("helvetica", "bold"); pdf.setTextColor(...GREY); pdf.text(k, M, y);
    pdf.setFont("helvetica", "normal"); pdf.setTextColor(...DARK);
    const vl = pdf.splitTextToSize(String(v), W - 2 * M - 190);
    pdf.text(vl, M + 190, y);
    y += Math.max(vl.length * 13, 15) + 7;
  });

  // Waste hierarchy declaration
  y += 6;
  pdf.setFillColor(249, 250, 251); const decl = "I confirm I have fulfilled my duty to apply the waste hierarchy as required by Regulation 12 of the Waste (England and Wales) Regulations 2011.";
  const dl = pdf.splitTextToSize(decl, W - 2 * M - 20);
  pdf.rect(M, y, W - 2 * M, dl.length * 12 + 18, "F");
  pdf.setFont("helvetica", "italic"); pdf.setFontSize(9); pdf.setTextColor(...DARK);
  pdf.text(dl, M + 10, y + 15); y += dl.length * 12 + 26;

  // Parties + signatures
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(9); pdf.setTextColor(...GREY);
  pdf.text("TRANSFEROR (FROM)", M, y);
  pdf.text("TRANSFEREE (TO)", W / 2 + 10, y);
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(10); pdf.setTextColor(...DARK);
  const addr = user.address || {};
  const fromLines = [user.business_name, [addr.line1, addr.city, addr.postcode].filter(Boolean).join(", ")].filter(Boolean);
  const toLines = [client.name, client.address].filter(Boolean);
  fromLines.forEach((l, i) => pdf.text(pdf.splitTextToSize(String(l), W / 2 - M - 20), M, y + 15 + i * 13));
  toLines.forEach((l, i) => pdf.text(pdf.splitTextToSize(String(l), W / 2 - M - 20), W / 2 + 10, y + 15 + i * 13));
  y += 56;

  const sigs = d.signatures || {};
  const drawSig = (dataUrl, x) => {
    if (dataUrl) { try { pdf.addImage(dataUrl, "PNG", x, y - 6, 150, 40); } catch (e) {} }
    pdf.setDrawColor(180, 180, 180); pdf.line(x, y + 38, x + 180, y + 38);
  };
  drawSig(sigs.transferor, M);
  drawSig(sigs.transferee, W / 2 + 10);
  pdf.setFontSize(8); pdf.setTextColor(...GREY);
  pdf.text("Transferor signature & date", M, y + 50);
  pdf.text("Transferee signature & date", W / 2 + 10, y + 50);
  return y + 58;
}

function labelize(k) {
  const map = { vat_no: "VAT", company_reg_no: "Reg", insurer: "Insurer", registration_body: "Body", registration_number: "Reg no", waste_carrier_licence: "Waste carrier", sic_code: "SIC", gas_safe_number: "Gas Safe" };
  return map[k] || k;
}

export async function sharePdf(pdf, filename, fallbackText) {
  const blob = pdf.output("blob");
  const file = new File([blob], filename, { type: "application/pdf" });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: filename, text: fallbackText || "" });
      return "shared";
    } catch (e) {
      if (e && e.name === "AbortError") return "aborted";
    }
  }
  pdf.save(filename);
  return "downloaded";
}

export function downloadPdf(pdf, filename) { pdf.save(filename); }

export function printPdf(pdf) {
  const url = pdf.output("bloburl");
  const w = window.open(url);
  if (w) w.onload = () => { try { w.print(); } catch (e) {} };
}
